import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { NumericInput } from '../common/NumericInput';
import { RadioCard, RadioGroup } from '../ui/RadioGroup';
import pt from '../../locales/pt';
import {
  payloadFromCategoryAllocation,
  resolveCategoryAllocation,
} from '../../utils/categoryAllocation';
import {
  CATEGORY_COLOR_PALETTE,
  DEFAULT_CATEGORY_COLOR,
} from '../../utils/categoryColors';
import { parsePtNumber } from '../../utils/money';
import { cn } from '../../utils/cn';

const schema = z.object({
  name: z.string().min(1, 'Nome obrigatório.'),
  budget_allocation: z.enum(['essential', 'lifestyle', 'personal_investment']),
  color: z.string().optional(),
  icon: z.string().optional(),
  monthly_limit: z.string().optional(),
});

const ALLOCATION_OPTIONS = [
  {
    value: 'essential',
    id: 'category-allocation-essential',
    labelKey: 'allocationEssential',
    hintKey: 'allocationEssentialHint',
  },
  {
    value: 'lifestyle',
    id: 'category-allocation-lifestyle',
    labelKey: 'allocationLifestyle',
    hintKey: 'allocationLifestyleHint',
  },
  {
    value: 'personal_investment',
    id: 'category-allocation-investment',
    labelKey: 'allocationInvestment',
    hintKey: 'allocationInvestmentHint',
  },
];

export function CategoryFormDialog({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isPending,
  createTitle,
  createDescription,
  defaultAllocation,
}) {
  const isEdit = Boolean(initialValues?.id);

  const { register, handleSubmit, control, reset, formState, setValue, watch } =
    useForm({
      resolver: zodResolver(schema),
      defaultValues: {
        name: '',
        budget_allocation: 'essential',
        color: DEFAULT_CATEGORY_COLOR,
        icon: '',
        monthly_limit: '',
      },
    });

  const selectedColor = watch('color');

  useEffect(() => {
    if (isOpen) {
      reset({
        name: initialValues?.name || '',
        budget_allocation:
          initialValues?.id
            ? resolveCategoryAllocation(initialValues)
            : defaultAllocation || 'essential',
        color: initialValues?.color || DEFAULT_CATEGORY_COLOR,
        icon: initialValues?.icon || '',
        monthly_limit: '',
      });
    }
  }, [isOpen, initialValues, defaultAllocation, reset]);

  const submit = (values) => {
    const payload = {
      name: values.name,
      ...payloadFromCategoryAllocation(values.budget_allocation),
      color: values.color,
      icon: values.icon,
    };
    if (!isEdit) {
      const limit = parsePtNumber(values.monthly_limit || '');
      if (limit && limit > 0) {
        payload.default_monthly_budget = limit.toFixed(2);
      }
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? pt.categories.form.titleEdit
              : createTitle || pt.categories.form.titleCreate}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? pt.categories.subtitle
              : createDescription || pt.categories.form.createDescription}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(submit)}>
          <div className="flex flex-col gap-1">
            <label htmlFor="category-name" className="text-sm font-medium text-muted-foreground">
              {pt.categories.form.nameLabel}
            </label>
            <Input
              id="category-name"
              placeholder={pt.categories.form.namePlaceholder}
              {...register('name')}
            />
            {formState.errors.name ? (
              <p className="text-sm text-error">{formState.errors.name.message}</p>
            ) : null}
          </div>

          <fieldset className="flex flex-col gap-2 rounded-2xl border border-border bg-background p-4">
            <legend className="px-1 text-sm font-medium text-foreground">
              {pt.categories.form.allocationQuestion}
            </legend>

            <Controller
              name="budget_allocation"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex flex-col gap-2"
                >
                  {ALLOCATION_OPTIONS.map((option) => (
                    <RadioCard
                      key={option.value}
                      value={option.value}
                      id={option.id}
                      label={pt.categories.form[option.labelKey]}
                      description={pt.categories.form[option.hintKey]}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-foreground">
              {pt.categories.form.colorLabel}
            </legend>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={pt.categories.form.colorLabel}>
              {CATEGORY_COLOR_PALETTE.map((color) => {
                const selected = selectedColor === color;
                return (
                  <button
                    key={color}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={color}
                    onClick={() => setValue('color', color, { shouldDirty: true })}
                    className={cn(
                      'h-8 w-8 rounded-full border-2 transition-shadow',
                      selected
                        ? 'border-foreground ring-2 ring-sage-400/60 ring-offset-2 ring-offset-background'
                        : 'border-transparent hover:border-border',
                    )}
                    style={{ backgroundColor: color }}
                  />
                );
              })}
            </div>
          </fieldset>

          {!isEdit ? (
            <div className="flex flex-col gap-1 rounded-2xl border border-dashed border-border bg-muted/30 p-4">
              <label
                htmlFor="category-monthly-limit"
                className="text-sm font-medium text-foreground"
              >
                {pt.categories.form.budget.sectionTitle}
              </label>
              <p className="text-xs text-muted-foreground">
                {pt.categories.form.budget.sectionHint}
              </p>
              <NumericInput
                id="category-monthly-limit"
                placeholder={pt.categories.form.budget.defaultMonthlyPlaceholder}
                className="mt-1"
                {...register('monthly_limit')}
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.common.cancel}
            </Button>
            <Button type="submit" disabled={isPending}>
              {pt.categories.form.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
